import React, { useEffect, useMemo, useRef, useState } from 'react';
import { gsap, MOTION_OK } from '../../animations/gsap';
import { VIDEO_PORTFOLIO_ENABLED } from '../../config/features';
import { getVideoPoster } from '../../utils/media';
import { BlockPrintPattern, Sparkle, Squiggle } from '../brand/Jaipur';
import {
  ArrowIcon,
  ReelTile,
  WhatsAppIcon,
  focusRing,
  getApiUrl,
  scrollToSection,
  useMediaQuery,
  whatsappLink,
} from './shared';
import './home.css';

/** Hand-picked portfolio order (same picks as the old UGC showcase), then the rest of the list. */
const SHOWCASE_INDICES = [0, 2, 8, 7, 13, 14, 12];
const MOBILE_MAX = 6;
const DESKTOP_MAX = 9;

const HERO_WHATSAPP = whatsappLink("Hi Apnaaapan, I saw your work and want to talk about my brand.");

function pickReels(list, max) {
  const valid = (Array.isArray(list) ? list : []).filter((v) => v && v.videoUrl);
  const picked = [];
  const used = new Set();
  SHOWCASE_INDICES.forEach((i) => {
    const v = list[i];
    if (v && v.videoUrl && !used.has(v.videoUrl)) {
      picked.push(v);
      used.add(v.videoUrl);
    }
  });
  valid.forEach((v) => {
    if (!used.has(v.videoUrl)) {
      picked.push(v);
      used.add(v.videoUrl);
    }
  });
  return picked.slice(0, max);
}

function ReelWall() {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const columnsCount = isDesktop ? 3 : 2;
  const max = isDesktop ? DESKTOP_MAX : MOBILE_MAX;
  const [videos, setVideos] = useState(null);
  const [soundKey, setSoundKey] = useState(null);
  const wallRef = useRef(null);

  useEffect(() => {
    if (!VIDEO_PORTFOLIO_ENABLED) {
      setVideos([]);
      return undefined;
    }
    let alive = true;
    fetch(getApiUrl('/api/videos'))
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((data) => alive && setVideos(Array.isArray(data.videos) ? data.videos : []))
      .catch(() => alive && setVideos([]));
    return () => {
      alive = false;
    };
  }, []);

  const columns = useMemo(() => {
    const cols = Array.from({ length: columnsCount }, () => []);
    const reels = videos ? pickReels(videos, max) : [];
    // While loading (or with no videos) show calm placeholder tiles of the same size: no layout shift.
    const tiles = reels.length ? reels : Array.from({ length: max }, (_, i) => ({ placeholder: true, id: `ph-${i}` }));
    tiles.forEach((tile, i) => cols[i % columnsCount].push(tile));
    return cols;
  }, [videos, columnsCount, max]);

  // Slow vertical drift, columns moving in opposite directions. Pauses when the hero is off screen.
  useEffect(() => {
    const wall = wallRef.current;
    if (!wall) return undefined;
    const mm = gsap.matchMedia();
    mm.add(MOTION_OK, () => {
      const cols = Array.from(wall.querySelectorAll('[data-wall-col]'));
      const tweens = cols.map((col, i) => {
        const travel = () => Math.max(0, col.scrollHeight - wall.clientHeight);
        const up = i % 2 === 0;
        return gsap.fromTo(
          col,
          { y: () => (up ? 0 : -travel()) },
          {
            y: () => (up ? -travel() : 0),
            duration: 26 + i * 4,
            ease: 'sine.inOut',
            repeat: -1,
            yoyo: true,
            invalidateOnRefresh: true,
          }
        );
      });
      let io;
      if (typeof IntersectionObserver !== 'undefined') {
        io = new IntersectionObserver(([entry]) => {
          tweens.forEach((t) => (entry.isIntersecting ? t.resume() : t.pause()));
        });
        io.observe(wall);
      }
      return () => io && io.disconnect();
    });
    return () => mm.revert();
  }, [columns]);

  return (
    <div
      ref={wallRef}
      className="relative h-[440px] sm:h-[520px] lg:h-[calc(100svh-7rem)] lg:max-h-[760px] lg:min-h-[500px] short:h-[calc(100svh-6rem)] short:min-h-[420px] overflow-hidden rounded-[28px]"
      aria-label="Client reels made by Apnaaapan"
      role="region"
    >
      <div className={`grid h-full gap-2.5 sm:gap-3 ${columnsCount === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
        {columns.map((col, ci) => (
          <div key={ci} data-wall-col className={`flex flex-col gap-2.5 sm:gap-3 ${ci === 1 ? 'pt-10' : ''}`}>
            {col.map((tile, ti) => {
              const key = tile.id || tile.videoUrl || `${ci}-${ti}`;
              if (tile.placeholder) {
                return (
                  <div
                    key={key}
                    aria-hidden="true"
                    className={`relative aspect-[9/16] w-full shrink-0 overflow-hidden rounded-2xl sm:rounded-3xl ${
                      (ci + ti) % 3 === 0 ? 'home-tint-saffron text-apn-orange' : (ci + ti) % 3 === 1 ? 'home-tint-pink text-apn-pink' : 'bg-apn-surface-2 text-apn-indigo'
                    }`}
                  >
                    <BlockPrintPattern opacity={0.18} size={44} />
                  </div>
                );
              }
              return (
                <ReelTile
                  key={key}
                  src={tile.videoUrl}
                  poster={getVideoPoster(tile.videoUrl)}
                  title={tile.title}
                  soundOn={soundKey === key}
                  onToggleSound={() => setSoundKey((cur) => (cur === key ? null : key))}
                  className="aspect-[9/16] w-full shrink-0 rounded-2xl sm:rounded-3xl"
                />
              );
            })}
          </div>
        ))}
      </div>
      <div aria-hidden="true" className="home-wall-fade-top pointer-events-none absolute inset-x-0 top-0 h-10" />
      <div aria-hidden="true" className="home-wall-fade-bottom pointer-events-none absolute inset-x-0 bottom-0 h-14" />
    </div>
  );
}

export default function HomeHero() {
  const onSeeWork = (e) => {
    if (scrollToSection('work')) e.preventDefault();
  };

  return (
    <section className="relative overflow-hidden bg-apn-bg text-apn-ink" aria-labelledby="home-hero-title">
      <div className="mx-auto grid max-w-7xl items-center gap-8 px-4 pb-10 pt-6 sm:px-6 sm:pt-10 lg:grid-cols-[1.05fr_1fr] lg:gap-12 lg:px-8 lg:pb-12 lg:pt-6 short:gap-8 short:pb-6 short:pt-2">
        <div className="relative z-10 max-w-2xl">
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-apn-line bg-apn-surface px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-apn-text sm:text-sm short:mb-3">
            <Sparkle className="h-3.5 w-3.5 text-apn-pink" />
            Content, brand &amp; growth studio · Jaipur
          </p>

          {/* The headline is the LCP element: no reveal animation, no web-font wait. */}
          <h1
            id="home-hero-title"
            className="font-display text-[clamp(3rem,13vw,4.75rem)] font-extrabold leading-[0.92] tracking-[-0.035em] text-apn-ink lg:text-[clamp(4.5rem,6.6vw,6.75rem)] short:text-[4.25rem]"
          >
            Brands that feel{' '}
            <span className="relative inline-block whitespace-nowrap text-apn-orange">
              apnaa.
              <Squiggle className="text-apn-saffron" />
            </span>
          </h1>

          <p className="mt-6 max-w-xl text-lg leading-relaxed text-apn-text sm:text-xl short:mt-4 short:text-lg">
            Reels, branding, websites and ads for founders who want to be noticed, trusted and bought from.
            Everything on this wall is client work.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center short:mt-5">
            <a
              href="#work"
              onClick={onSeeWork}
              data-cta="home-hero-see-work"
              className={`inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-apn-ink px-7 text-base font-semibold text-apn-bg transition hover:-translate-y-0.5 hover:bg-apn-orange hover:text-white ${focusRing}`}
            >
              See the work
              <ArrowIcon direction="down" className="h-5 w-5" />
            </a>
            <a
              href={HERO_WHATSAPP}
              target="_blank"
              rel="noopener noreferrer"
              data-cta="home-hero-whatsapp"
              className={`inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full border-2 border-apn-ink px-7 text-base font-semibold text-apn-ink transition hover:-translate-y-0.5 hover:border-apn-orange hover:text-apn-orange ${focusRing}`}
            >
              <WhatsAppIcon className="h-5 w-5" />
              WhatsApp us
            </a>
          </div>
          <p className="mt-4 text-sm text-apn-muted">Tap any reel to hear it.</p>
        </div>

        <ReelWall />
      </div>
    </section>
  );
}
